/**
 * Branding context for the admin portal.
 *
 * Mirrors the customer portal's lib/branding.tsx — on app load we hit
 * the public /api/branding/ endpoint, push the result into a React
 * context, and apply document.title + favicon + CSS-var side effects.
 *
 * Even though the admin portal is reseller-internal, it shares the
 * same brand: when a reseller sets a logo, they expect to see it in
 * their own admin too.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

// Same defaults as the backend get_branding() hardcoded fallback.
export const DEFAULT_BRANDING = {
  app_name: 'Digital Employees',
  app_tagline: '',
  company_legal_name: 'Digital Employees',
  primary_color: '#1e40af',
  primary_color_dark: '#1e3a8a',
  support_email: 'support@digitalemployees.us',
  support_url: '',
  company_url: 'https://digitalemployees.us',
  terms_url: '',
  privacy_url: '',
  from_email: '',
  from_name: '',
  reply_to_email: '',
  footer_text: '',
  footer_text_rendered: '',
  logo_primary_url: '',
  logo_square_url: '',
  favicon_url: '',
  login_banner_url: '',
};

const BrandingContext = createContext(DEFAULT_BRANDING);

export function useBranding() {
  return useContext(BrandingContext);
}

function applyDocumentSideEffects(b) {
  if (b.app_name) {
    document.title = `${b.app_name} Admin`;
  }
  const fav = b.favicon_url || b.logo_square_url;
  if (fav) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = fav;
  }
  const root = document.documentElement;
  if (b.primary_color) root.style.setProperty('--brand-primary', b.primary_color);
  if (b.primary_color_dark) root.style.setProperty('--brand-primary-dark', b.primary_color_dark);
}

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT_BRANDING);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/branding/')
      .then((res) => {
        if (cancelled) return;
        const merged = { ...DEFAULT_BRANDING, ...res.data };
        setBrand(merged);
        applyDocumentSideEffects(merged);
      })
      .catch(() => {
        applyDocumentSideEffects(DEFAULT_BRANDING);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <BrandingContext.Provider value={brand}>{children}</BrandingContext.Provider>;
}
